import clsx from "clsx"
import { FormikProps, useFormikContext } from "formik"
import { FC, useEffect } from "react"
import { KTSVG } from "../../../_metronic/helpers"
import { useLoadingModalState } from "../dialogs"


type Props = {
  doClose: Function
  submitText?: string
  cancelText?: string
  showDebug?: boolean    
  align?: ('center'|'left'|'right') 
  savingDraft?: boolean
  onDraftClick?: () => void
}

const SubmitButtonRow: FC<Props> = ({
  doClose, 
  submitText = 'Submit',
  cancelText = 'Cancel',
  showDebug = false,
  align = 'center',  
  savingDraft= false,  
  onDraftClick
}) => {

  const { toggleShow, clear } = useLoadingModalState(state => state);
  const { values, errors, isSubmitting, isValid, touched } = useFormikContext(); 
 
  // toggle LoadingModal
  useEffect(() => {
    if (! (isSubmitting)) {
      clear();
      return;
    }
    toggleShow(true);
    return (() => clear());
  // eslint-disable-next-line  
  }, [isSubmitting]);

  return (
  <>
  <div className={`text-${align} mt-4`}>
    <button
      type='reset'
      onClick={()=> { doClose()}}
      className='btn btn-light-secondary me-3'
      disabled={isSubmitting}
    >
      {cancelText ? cancelText : 'Cancel'}
    </button>

    { (onDraftClick) &&
    <button
      type='button'
      className={clsx('btn text-muted me-3',
        {'btn-outline-warning' : (! savingDraft)},
        {'btn-warning' : savingDraft},
      )}
      onClick={onDraftClick}        
      disabled={isSubmitting || savingDraft}
    >
      { (savingDraft) ? (
      <>
      Saving ...{' '}
      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
      </>
      ) : (
      <>
      <KTSVG path='/media/icons/duotune/arrows/arr037.svg' className='svg-icon-4 me-1' />
      Save Draft
      </>
      ) }
    </button>
    }
    <button
      type='submit'
      className='btn btn-primary'
      disabled={isSubmitting || !isValid || !touched}
    >
      {!isSubmitting && <span className='indicator-label'>{submitText ? submitText : 'Submit'}</span>}
      {isSubmitting && (
      <span className='indicator-progress' style={{display: 'block'}}>
        Please wait...
        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
      </span>
      )}
    </button>
  </div>
  { (showDebug && (process.env.NODE_ENV === 'development')) && 
  <div className="row fs-8">
    <div className="col-12" style={{wordBreak: 'break-all'}}>
      {JSON.stringify(values)}
    </div>
    <div className="col-12 text-danger" style={{wordBreak: 'break-all'}}>
      {JSON.stringify(errors)}
    </div>
  </div>    
  }
  </>
  )
}

type ComponentProps = {
  formik: FormikProps<any>
  onCancel: () => void
  cancelText?: 'Discard' | 'Cancel'
  submitText?: string
  disabled?: boolean
  showDebug?: boolean
}

const SubmitButtonComponent: FC<ComponentProps> = ({
  formik,
  onCancel,
  cancelText = 'Discard',
  submitText = 'Submit',
  disabled = false,
  showDebug
}) => {
  
  const { toggleShow, clear } = useLoadingModalState(state => state);
  
  // toggle LoadingModal
  useEffect(() => {
    if (! (formik && formik.isSubmitting)) {
      clear();
      return;
    }
    toggleShow(true);
    return (() => clear());
  // eslint-disable-next-line  
  }, [formik]);
  
  return (
  <>
    <div className='text-center pt-8'>
      <button
        type='reset'
        onClick={onCancel}
        className='btn btn-light me-3'
        disabled={formik.isSubmitting || disabled}
      >
        {cancelText}
      </button>

      <button
        type='submit'
        className='btn btn-primary'
        disabled={formik.isSubmitting || !formik.isValid || disabled}
      >
        <span className='indicator-label'>{submitText}</span>
        {(formik.isSubmitting || disabled) && (
        <span className='indicator-progress'>
            Please wait...{' '}
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
        </span>
        )}
      </button>
    </div>
    { (showDebug && (process.env.NODE_ENV === 'development')) && 
    <div className="row fs-8">
      <div className="col-12" style={{wordBreak: 'break-all'}}>
        {JSON.stringify(formik.values)}
      </div>
      <div className="col-12 text-danger" style={{wordBreak: 'break-all'}}>
        {JSON.stringify(formik.errors)}
      </div>
    </div>    
    }
  </>
  )
}


export {SubmitButtonRow, SubmitButtonComponent}

